import type {
  FunnelImpact,
  FunnelMomentCount,
  FunnelMomentImpact,
  FunnelMomentStageCount,
  FunnelOutcomeExitCount,
  FunnelStageCounts,
} from "@repo/app-types/api/funnel";
import type {
  FunnelMoments,
  FunnelStages,
} from "@repo/app-types/entities/funnel";
import type { OutcomeGreenRedYellowResultCategory } from "@repo/app-types/entities/outcome";

const shareFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 0,
});

/** A share in 0..1 as a whole percentage, or a dash. */
export function formatFunnelShare(share: number | null): string {
  return share === null ? "–" : shareFormat.format(share);
}

const preciseShareFormat = new Intl.NumberFormat("en-US", {
  style: "percent",
  maximumFractionDigits: 1,
});

/** A share in 0..1 as a percentage to one decimal. */
export function formatFunnelPreciseShare(share: number): string {
  return preciseShareFormat.format(share);
}

/** The name of the stage an exchange happened in; null is before any stage. */
export function formatFunnelStageName(
  stages: FunnelStages,
  stageKey: string | null,
): string {
  if (stageKey === null) {
    const firstStage = stages[0];
    return firstStage ? `Before ${firstStage.name}` : "Before any stage";
  }
  return stages.find((stage) => stage.key === stageKey)?.name ?? stageKey;
}

/**
 * A stage's interactions in prose: "‘Offer’ interactions", "interactions
 * before ‘Greeting’", or "interactions" for every stage.
 */
export function formatFunnelStageInteractions(
  stages: FunnelStages,
  stageKey: FunnelStageFocus,
): string {
  if (stageKey === undefined) {
    return "interactions";
  }
  if (stageKey === null) {
    const firstStage = stages[0];
    return firstStage
      ? `interactions before ‘${firstStage.name}’`
      : "interactions before any stage";
  }
  return `‘${formatFunnelStageName(stages, stageKey)}’ interactions`;
}

/** Interactions that reached a stage; before the first stage, all of them. */
function findFunnelStageInteractions(
  stageCounts: FunnelStageCounts,
  stageKey: string | null,
): number {
  if (stageKey === null) {
    return stageCounts.denominator;
  }
  return (
    stageCounts.stages.find((stage) => stage.stage_key === stageKey)?.reached ??
    0
  );
}

/** The interactions that went no further than a stage, by outcome; null is before the first stage. */
export function findFunnelStageExitsByOutcome(
  stageCounts: FunnelStageCounts,
  stageKey: string | null,
): FunnelOutcomeExitCount[] {
  if (stageKey === null) {
    return stageCounts.reached_no_stage_by_outcome;
  }
  return (
    stageCounts.stages.find((stage) => stage.stage_key === stageKey)
      ?.exited_by_outcome ?? []
  );
}

/** An outcome result's label, or "No outcome" for interactions without one. */
export function formatFunnelOutcomeLabel(label: string | null): string {
  return label ?? "No outcome";
}

/**
 * A color the flow and the bars draw in: continued, an outcome result's
 * category, or without an outcome.
 */
export type FunnelColor =
  | "continued"
  | OutcomeGreenRedYellowResultCategory
  | "none";

/** Each color's classes, written out so Tailwind sees them. */
export const funnelColorClassNames: Record<
  FunnelColor,
  { background: string; fill: string; stroke: string }
> = {
  continued: {
    background: "bg-indigo-400",
    fill: "fill-indigo-400",
    stroke: "stroke-indigo-400",
  },
  green: {
    background: "bg-green-400",
    fill: "fill-green-400",
    stroke: "stroke-green-400",
  },
  red: {
    background: "bg-red-400",
    fill: "fill-red-400",
    stroke: "stroke-red-400",
  },
  yellow: {
    background: "bg-yellow-400",
    fill: "fill-yellow-400",
    stroke: "stroke-yellow-400",
  },
  gray: {
    background: "bg-gray-400",
    fill: "fill-gray-400",
    stroke: "stroke-gray-400",
  },
  none: {
    background: "bg-gray-300",
    fill: "fill-gray-300",
    stroke: "stroke-gray-300",
  },
};

/** The color an outcome category is drawn in; no outcome is a lighter gray. */
export function findFunnelOutcomeColor(
  category: OutcomeGreenRedYellowResultCategory | null,
): FunnelColor {
  return category ?? "none";
}

/** Exits are drawn green, red, yellow, gray, then without an outcome. */
const funnelOutcomeCategoryOrder: readonly OutcomeGreenRedYellowResultCategory[] =
  ["green", "red", "yellow", "gray"];

function rankFunnelOutcomeCategory(
  category: OutcomeGreenRedYellowResultCategory | null,
): number {
  return category === null
    ? funnelOutcomeCategoryOrder.length
    : funnelOutcomeCategoryOrder.indexOf(category);
}

/** Exits in category order, the largest first within a category. */
export function sortFunnelExitsByCategory(
  exits: readonly FunnelOutcomeExitCount[],
): FunnelOutcomeExitCount[] {
  return [...exits].sort(
    (a, b) =>
      rankFunnelOutcomeCategory(a.category) -
        rankFunnelOutcomeCategory(b.category) ||
      b.interactions - a.interactions,
  );
}

export const FunnelFlowNodeKind = {
  Start: "start",
  Stage: "stage",
  Exit: "exit",
} as const;

export type FunnelFlowNodeKind =
  (typeof FunnelFlowNodeKind)[keyof typeof FunnelFlowNodeKind];

export interface FunnelFlowNode {
  kind: FunnelFlowNodeKind;
  name: string;
  /** Interactions. */
  value: number;
  /** The stage selecting this node shows; null is before the first stage. */
  stageKey: string | null;
  color: FunnelColor;
}

export interface FunnelFlowLink {
  source: number;
  target: number;
  /** Interactions. */
  value: number;
  isExit: boolean;
  /** The source node's stage. */
  stageKey: string | null;
  /** The target node's. */
  color: FunnelColor;
}

export interface FunnelFlowLegendEntry {
  label: string;
  color: FunnelColor;
}

export interface FunnelFlowData {
  nodes: FunnelFlowNode[];
  links: FunnelFlowLink[];
  /** Continued, the success stage when the last one is, then the exits' outcomes. */
  legend: FunnelFlowLegendEntry[];
}

/**
 * The Sankey of one cohort's interactions: a start node, then each stage, each
 * but the last with an exit per outcome for the interactions that went no
 * further, below the link to the next stage in category order. A last stage
 * that is the outcome's success result is drawn in the success color. Links
 * worth nothing are left out, and so are the nodes that leaves unconnected.
 */
export function buildFunnelFlowData(
  stages: FunnelStages,
  stageCounts: FunnelStageCounts,
): FunnelFlowData {
  const countByKey = new Map(
    stageCounts.stages.map((stageCount) => [stageCount.stage_key, stageCount]),
  );
  const successColor = findFunnelOutcomeColor("green");
  const stageNodes = stages.map((stage): FunnelFlowNode => ({
    kind: FunnelFlowNodeKind.Stage,
    name: stage.name,
    value: countByKey.get(stage.key)?.reached ?? 0,
    stageKey: stage.key,
    color: stage.is_outcome_success ? successColor : "continued",
  }));
  const startNode: FunnelFlowNode = {
    kind: FunnelFlowNodeKind.Start,
    name: "All interactions",
    value: stageCounts.denominator,
    stageKey: null,
    color: "continued",
  };
  // The start node, then one per stage.
  const flowNodes = [startNode, ...stageNodes];

  const nodes: FunnelFlowNode[] = [...flowNodes];
  const links: FunnelFlowLink[] = [];
  const exitLegend = new Map<
    string,
    FunnelFlowLegendEntry & { categoryRank: number }
  >();
  flowNodes.forEach((node, index) => {
    const nextNode = flowNodes[index + 1];
    if (!nextNode) {
      return;
    }
    links.push({
      source: index,
      target: index + 1,
      value: nextNode.value,
      isExit: false,
      stageKey: node.stageKey,
      color: nextNode.color,
    });
    for (const exit of sortFunnelExitsByCategory(
      findFunnelStageExitsByOutcome(stageCounts, node.stageKey),
    )) {
      const label = formatFunnelOutcomeLabel(exit.label);
      const color = findFunnelOutcomeColor(exit.category);
      exitLegend.set(`${exit.category ?? ""}:${label}`, {
        label,
        color,
        categoryRank: rankFunnelOutcomeCategory(exit.category),
      });
      nodes.push({
        kind: FunnelFlowNodeKind.Exit,
        name: label,
        value: exit.interactions,
        stageKey: node.stageKey,
        color,
      });
      links.push({
        source: index,
        target: nodes.length - 1,
        value: exit.interactions,
        isExit: true,
        stageKey: node.stageKey,
        color,
      });
    }
  });

  const nonZeroLinks = links.filter((link) => link.value > 0);
  const connected = new Set(
    nonZeroLinks.flatMap((link) => [link.source, link.target]),
  );
  const newIndexByOldIndex = new Map<number, number>();
  const connectedNodes = nodes.filter((_node, index) => {
    if (!connected.has(index)) {
      return false;
    }
    newIndexByOldIndex.set(index, newIndexByOldIndex.size);
    return true;
  });
  const lastStage = stages.at(-1);
  const fixedLegend: FunnelFlowLegendEntry[] = [
    { label: "Continued", color: "continued" },
    ...(lastStage?.is_outcome_success
      ? [{ label: lastStage.name, color: successColor }]
      : []),
  ];
  return {
    nodes: connectedNodes,
    links: nonZeroLinks.map((link) => ({
      ...link,
      source: newIndexByOldIndex.get(link.source) ?? link.source,
      target: newIndexByOldIndex.get(link.target) ?? link.target,
    })),
    legend: [
      ...fixedLegend,
      ...[...exitLegend.values()]
        .sort((a, b) => a.categoryRank - b.categoryRank)
        .map(({ label, color }) => ({ label, color })),
    ],
  };
}

/**
 * The stage a moment table shows: a stage key, null before the first stage,
 * or undefined for every stage.
 */
export type FunnelStageFocus = string | null | undefined;

/** What the moment table's numbers are taken from, for one cohort. */
export interface FunnelMomentCohortCounts {
  momentCounts: readonly FunnelMomentStageCount[];
  momentTotals: readonly FunnelMomentCount[];
  stageCounts: FunnelStageCounts;
}

/** A part of a moment's interactions in its bar: continued, or ended with an outcome. */
export interface FunnelMomentPart {
  key: string;
  /** "Continued", "Had this moment" in the last stage, else the outcome's label. */
  label: string;
  color: FunnelColor;
  interactions: number;
}

/** One moment in the focused stage, or in every stage, for the agent cohort. */
export interface FunnelMomentRow {
  momentKey: string;
  momentName: string;
  momentDescription: string;
  interactions: number;
  /**
   * In a stage, continued then the drop-offs by outcome, or in the last stage
   * one part; in every stage, by outcome. Sums to `interactions`.
   */
  parts: FunnelMomentPart[];
  /** Interactions that reached the stage; in every stage, all of them. */
  stageInteractions: number;
  /** `interactions` over `stageInteractions`. */
  share: number;
  /**
   * The drop-offs over `interactions`; null in the last stage, where every
   * interaction ends, and in every stage.
   */
  dropOffRate: number | null;
  /** What the moment cost in the focused stage, or in every stage; null if nothing. */
  impact: FunnelImpact | null;
  /** 1-based, the server's ranking in the focused stage or overall; null if unranked. */
  rank: number | null;
}

/** Interactions a stage focus is out of: the stage's, or all of them. */
function findFunnelFocusInteractions(
  stageCounts: FunnelStageCounts,
  stageKey: FunnelStageFocus,
): number {
  return stageKey === undefined
    ? stageCounts.denominator
    : findFunnelStageInteractions(stageCounts, stageKey);
}

function buildFunnelOutcomeParts(
  outcomes: readonly FunnelOutcomeExitCount[],
): FunnelMomentPart[] {
  return sortFunnelExitsByCategory(outcomes).map((outcome) => ({
    key: `${outcome.category ?? ""}:${outcome.label ?? ""}`,
    label: formatFunnelOutcomeLabel(outcome.label),
    color: findFunnelOutcomeColor(outcome.category),
    interactions: outcome.interactions,
  }));
}

/** A moment's interactions in a stage, or in every stage, as the table shows them. */
function buildFunnelMomentCount(input: {
  counts: FunnelMomentCohortCounts;
  momentKey: string;
  stageKey: FunnelStageFocus;
  isLastStage: boolean;
}): Pick<FunnelMomentRow, "interactions" | "parts" | "dropOffRate"> | null {
  const { counts, momentKey, stageKey, isLastStage } = input;
  if (stageKey === undefined) {
    const total = counts.momentTotals.find(
      (candidate) => candidate.moment_key === momentKey,
    );
    return total
      ? {
          interactions: total.interactions,
          parts: buildFunnelOutcomeParts(total.by_outcome),
          dropOffRate: null,
        }
      : null;
  }
  const count = counts.momentCounts.find(
    (candidate) =>
      candidate.moment_key === momentKey && candidate.stage_key === stageKey,
  );
  if (!count) {
    return null;
  }
  return {
    interactions: count.interactions,
    parts: isLastStage
      ? [
          {
            key: "continued",
            label: "Had this moment",
            color: "continued",
            interactions: count.interactions,
          },
        ]
      : [
          {
            key: "continued",
            label: "Continued",
            color: "continued",
            interactions: count.continued,
          },
          ...buildFunnelOutcomeParts(count.dropped_off_by_outcome),
        ],
    dropOffRate: isLastStage ? null : count.dropped_off / count.interactions,
  };
}

/**
 * The moments that came up in a stage, or in any stage, in the server's
 * ranking there (by unrounded `rank_score`), then the most interactions. `impacts` are the
 * cohort's moment ranking, each moment with its stages.
 */
export function buildFunnelMomentRows(input: {
  stages: FunnelStages;
  moments: FunnelMoments;
  counts: FunnelMomentCohortCounts;
  impacts: readonly FunnelMomentImpact[];
  stageKey: FunnelStageFocus;
}): FunnelMomentRow[] {
  const { stages, moments, counts, impacts, stageKey } = input;
  const stageInteractions = findFunnelFocusInteractions(
    counts.stageCounts,
    stageKey,
  );
  const isLastStage =
    stageKey !== undefined &&
    stageKey !== null &&
    stages.at(-1)?.key === stageKey;
  const impactByMomentKey = new Map(
    impacts.map((impact, index) => [impact.moment_key, { impact, index }]),
  );
  const findImpact = (
    momentKey: string,
  ): { impact: FunnelImpact; rank: number } | null => {
    const entry = impactByMomentKey.get(momentKey);
    if (!entry) {
      return null;
    }
    if (stageKey === undefined) {
      return { impact: entry.impact, rank: entry.index + 1 };
    }
    const stage = entry.impact.stages.find(
      (candidate) => candidate.stage_key === stageKey,
    );
    return stage ? { impact: stage, rank: stage.stage_rank } : null;
  };

  return moments
    .flatMap((moment): FunnelMomentRow[] => {
      const count = buildFunnelMomentCount({
        counts,
        momentKey: moment.key,
        stageKey,
        isLastStage,
      });
      if (!count || count.interactions === 0 || stageInteractions === 0) {
        return [];
      }
      const ranked = findImpact(moment.key);
      return [
        {
          momentKey: moment.key,
          momentName: moment.name,
          momentDescription: moment.description,
          ...count,
          stageInteractions,
          share: count.interactions / stageInteractions,
          impact: ranked?.impact ?? null,
          rank: ranked?.rank ?? null,
        },
      ];
    })
    .sort(
      (a, b) =>
        (a.rank ?? Infinity) - (b.rank ?? Infinity) ||
        b.interactions - a.interactions,
    );
}

/** A cohort's share of a stage's interactions, or of all, with a moment; null without any. */
export function computeFunnelMomentShare(input: {
  counts: FunnelMomentCohortCounts;
  momentKey: string;
  stageKey: FunnelStageFocus;
}): number | null {
  const { counts, momentKey, stageKey } = input;
  const count = buildFunnelMomentCount({
    counts,
    momentKey,
    stageKey,
    isLastStage: false,
  });
  const stageInteractions = findFunnelFocusInteractions(
    counts.stageCounts,
    stageKey,
  );
  if (!count || count.interactions === 0 || stageInteractions === 0) {
    return null;
  }
  return count.interactions / stageInteractions;
}

export interface FunnelMomentStageShare {
  /** Null is before the first stage. */
  stageKey: string | null;
  stageName: string;
  /** Of the stage's interactions, those with the moment. */
  share: number;
}

/**
 * Each column of the flow's share of interactions with a moment: before the
 * first stage (the flow's start node), then each stage in order; 0 where it
 * never came up.
 */
export function computeFunnelMomentStageShares(input: {
  stages: FunnelStages;
  counts: FunnelMomentCohortCounts;
  momentKey: string;
}): FunnelMomentStageShare[] {
  const { stages, counts, momentKey } = input;
  return [null, ...stages.map((stage) => stage.key)].map((stageKey) => ({
    stageKey,
    stageName: formatFunnelStageName(stages, stageKey),
    share: computeFunnelMomentShare({ counts, momentKey, stageKey }) ?? 0,
  }));
}

/**
 * Interactions that can succeed: all but those whose outcome is not
 * applicable. The success rate a moment's lift moves is out of these.
 */
export function countFunnelApplicableInteractions(
  stageCounts: FunnelStageCounts,
): number {
  const notApplicable = [
    ...stageCounts.reached_no_stage_by_outcome,
    ...stageCounts.stages.flatMap((stage) => stage.exited_by_outcome),
  ]
    .filter((exit) => exit.category === "gray")
    .reduce((sum, exit) => sum + exit.interactions, 0);
  return stageCounts.denominator - notApplicable;
}

/** One decimal, a trailing zero included, like the server's rounding. */
const liftFormat = new Intl.NumberFormat("en-US", {
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

/** Successes as a lift: "+4.0", or a dash below `FUNNEL_MIN_SHOWN_LIFT`. */
export function formatFunnelLift(successes: number): string {
  return checkFunnelLiftShown(successes)
    ? `+${liftFormat.format(successes)}`
    : "–";
}

/** Below this many successes, a lift is too small to tell apart from noise. */
const FUNNEL_MIN_SHOWN_LIFT = 1.5;

export function checkFunnelLiftShown(successes: number): boolean {
  return successes >= FUNNEL_MIN_SHOWN_LIFT;
}

/** A share in 0..1 as percentage points: "+1.3 pts". */
export function formatFunnelPoints(share: number): string {
  return `+${liftFormat.format(share * 100)} pts`;
}
